import React, { useContext, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Login from './pages/Login';
import Register from './pages/Register';
import MemberDashboard from './pages/MemberDashboard';
import AdminDashboard from './pages/AdminDashboard';
import { AuthProvider, AuthContext } from './context/AuthContext';

function PageTransition({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18, scale: 0.995 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -12, scale: 0.995 }}
      transition={{ duration: 0.28, ease: 'easeOut' }}
      style={{ minHeight: '100vh' }}
    >
      {children}
    </motion.div>
  );
}

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return null;
}

function PrivateRoute({ children, role }) {
  const { user } = useContext(AuthContext);

  if (!user) return <Navigate to="/" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;

  return <PageTransition>{children}</PageTransition>;
}

function PublicRoute({ children }) {
  const { user } = useContext(AuthContext);

  if (user?.role === 'admin') return <Navigate to="/admin" replace />;
  if (user?.role === 'member') return <Navigate to="/member" replace />;

  return <PageTransition>{children}</PageTransition>;
}

function NotFound() {
  return (
    <PageTransition>
      <div style={styles.notFoundPage}>
        <div style={styles.notFoundCard}>
          <div style={styles.notFoundCode}>404</div>
          <h1 style={styles.notFoundTitle}>Page not found</h1>
          <p style={styles.notFoundText}>
            The page you are trying to access does not exist or has been moved.
          </p>
          <a href="/" style={styles.notFoundButton}>
            Go to Login
          </a>
        </div>
      </div>
    </PageTransition>
  );
}

function AppRoutes() {
  const location = useLocation();

  return (
    <>
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />

          <Route
            path="/member"
            element={
              <PrivateRoute role="member">
                <MemberDashboard />
              </PrivateRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <PrivateRoute role="admin">
                <AdminDashboard />
              </PrivateRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>
    </>
  );
}

const App = () => {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
};

const styles = {
  notFoundPage: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background:
      'radial-gradient(circle at top left, rgba(34,197,94,0.14), transparent 24%), linear-gradient(180deg, #f7fff9 0%, #f8fafc 100%)',
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  notFoundCard: {
    width: '100%',
    maxWidth: '520px',
    padding: '36px 28px',
    borderRadius: '28px',
    background: 'rgba(255,255,255,0.9)',
    border: '1px solid rgba(15,23,42,0.06)',
    boxShadow: '0 24px 60px rgba(15,23,42,0.08)',
    textAlign: 'center',
  },
  notFoundCode: {
    fontSize: '64px',
    fontWeight: 900,
    lineHeight: 1,
    color: '#16a34a',
    marginBottom: '12px',
  },
  notFoundTitle: {
    margin: '0 0 10px',
    fontSize: '28px',
    fontWeight: 800,
    color: '#0f172a',
  },
  notFoundText: {
    margin: '0 0 22px',
    fontSize: '14px',
    lineHeight: 1.7,
    color: '#64748b',
  },
  notFoundButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '48px',
    padding: '0 20px',
    borderRadius: '14px',
    textDecoration: 'none',
    background: 'linear-gradient(135deg, #16a34a, #166534)',
    color: '#fff',
    fontWeight: 800,
    boxShadow: '0 14px 30px rgba(22,163,74,0.22)',
  },
};

export default App;